import { PageHeader } from "@/components/layout/header-provider";
import { ManualFilter } from "@/components/shadcn-studio/blocks/datatable-account";
import UserDatatable from "@/components/shadcn-studio/blocks/datatable-user";
import { useDebounce } from "@/lib/hooks/use-debounce";
import { api as trpc, trpcClient } from "@/lib/trpc";
import { type RoleType } from "@repo/core";
import {
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from "@repo/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

const ALLOWED_SORTS = ["name", "email", "role", "createdAt", "status"] as const;
const ALLOWED_SORT_DIRS = ["asc", "desc"] as const;
const ALLOWED_ACCOUNT_TYPES = [
  "creator",
  "agency",
  "soft_deleted",
  "no_account",
] as const;

type SortBy = (typeof ALLOWED_SORTS)[number];
type SortDir = (typeof ALLOWED_SORT_DIRS)[number];
type AccountType = (typeof ALLOWED_ACCOUNT_TYPES)[number];

export const Route = createFileRoute("/admin/users")({
  validateSearch: (
    search,
  ): {
    page: number;
    sortBy: SortBy | undefined;
    sortDir: SortDir | undefined;
    accountType: AccountType | undefined;
  } => {
    const rawPage = Number(search.page);
    const page = Number.isInteger(rawPage) && rawPage > 0 ? rawPage : 1;

    const rawSortBy = search.sortBy;
    const sortBy = (ALLOWED_SORTS as readonly SortBy[]).includes(
      rawSortBy as SortBy,
    )
      ? (rawSortBy as SortBy)
      : undefined;

    const rawSortDir = search.sortDir;
    const sortDir = (ALLOWED_SORT_DIRS as readonly SortDir[]).includes(
      rawSortDir as SortDir,
    )
      ? (rawSortDir as SortDir)
      : undefined;

    const rawAccountType = search.accountType;
    const accountType = (
      ALLOWED_ACCOUNT_TYPES as readonly AccountType[]
    ).includes(rawAccountType as AccountType)
      ? (rawAccountType as AccountType)
      : undefined;

    // Normalize: sortDir has no meaning without a sortBy
    return { page, sortBy, sortDir: sortBy ? sortDir : undefined, accountType };
  },
  component: UsersPage,
});

function UsersPage() {
  const { page, sortBy, sortDir, accountType } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const setPage = (newPage: number) =>
    navigate({ search: (prev) => ({ ...prev, page: newPage }) });
  const setSortBy = (col: string | undefined, dir: "asc" | "desc") => {
    const validSortBy = (ALLOWED_SORTS as readonly SortBy[]).includes(
      col as SortBy,
    )
      ? (col as SortBy)
      : undefined;
    navigate({
      search: (prev) => ({
        ...prev,
        sortBy: validSortBy,
        // Clear sortDir when there's no valid sortBy so the URL stays clean
        sortDir: validSortBy ? dir : undefined,
        page: 1,
      }),
    });
  };
  const [search, setSearch] = useState("");
  const [role, setRole] = useState<RoleType | "all">("all");
  const [status, setStatus] = useState<"active" | "inactive" | "all">("all");

  const [restoreTarget, setRestoreTarget] = useState<{
    orgId: string;
    orgName: string;
  } | null>(null);
  const [conflictEmails, setConflictEmails] = useState<string[] | null>(null);

  const queryClient = useQueryClient();
  const debouncedSearch = useDebounce(search, 500);

  const { data, isLoading } = useQuery(
    trpc.admin.getUsers.queryOptions(
      {
        page,
        limit: 25,
        search: debouncedSearch || undefined,
        role: role === "all" ? undefined : role,
        status: status === "all" ? undefined : status,
        accountType: accountType ?? undefined,
        sortBy: sortBy,
        sortDir: sortBy ? sortDir : undefined,
      },
      {
        placeholderData: (prev) => prev,
      },
    ),
  );

  const { mutate: updateUserRole } = useMutation({
    mutationFn: (vars: { userId: string; role: string }) =>
      trpcClient.admin.updateUserRole.mutate({
        userId: vars.userId,
        role: vars.role as RoleType,
      }),
    onSuccess: () => {
      toast.success("User role updated");
      queryClient.invalidateQueries({
        queryKey: trpc.admin.getUsers.queryKey(),
      });
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  const { mutate: resetUserPassword } = useMutation({
    mutationFn: (vars: { userId: string }) =>
      trpcClient.admin.resetUserPassword.mutate(vars),
    onSuccess: () => {
      toast.success("Password has been removed for this user");
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  const { mutate: resetUser2FA } = useMutation({
    mutationFn: (vars: { userId: string }) =>
      trpcClient.admin.resetUser2FA.mutate(vars),
    onSuccess: () => {
      toast.success("2FA has been disabled for this user");
      queryClient.invalidateQueries({
        queryKey: trpc.admin.getUsers.queryKey(),
      });
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  const { mutate: createUserAccount } = useMutation({
    mutationFn: (vars: { userId: string }) =>
      trpcClient.admin.createUserAccount.mutate(vars),
    onSuccess: () => {
      toast.success("Account created for user");
      queryClient.invalidateQueries({
        queryKey: trpc.admin.getUsers.queryKey(),
      });
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  const closeRestoreDialog = () => {
    setRestoreTarget(null);
    setConflictEmails(null);
  };

  const { mutate: restoreUserAccount, isPending: isRestoringAccount } =
    useMutation({
      mutationFn: (vars: { orgId: string; force?: boolean }) =>
        trpcClient.admin.restoreUserAccount.mutate(vars),
      onSuccess: (data) => {
        if (data.conflicts && data.conflicts.length > 0) {
          toast.success(
            `Account restored. ${data.conflicts.length} profile(s) need email re-verification: ${data.conflicts.join(", ")}`,
          );
        } else {
          toast.success("Account restored successfully");
        }
        closeRestoreDialog();
        queryClient.invalidateQueries({
          queryKey: trpc.admin.getUsers.queryKey(),
        });
      },
      onError: (err) => {
        // If CONFLICT error, parse emails and show conflict dialog
        if (err.message.includes("conflict") && restoreTarget) {
          // Extract emails from brackets in the error message
          const emailMatch = err.message.match(/\[([^\]]+)\]/);
          const emails = emailMatch ? emailMatch[1].split(", ") : [];
          if (emails.length > 0) {
            setConflictEmails(emails);
          } else {
            toast.error(err.message);
          }
        } else {
          toast.error(err.message);
        }
      },
    });

  return (
    <div className="flex flex-col gap-6 p-6 max-md:p-0 h-full">
      <PageHeader>
        <span className="font-semibold">Users</span>
      </PageHeader>

      <Card className="flex-1 flex flex-col overflow-hidden max-md:border-0 max-md:shadow-none max-md:bg-transparent max-md:rounded-none">
        <div className="p-4 border-b flex flex-col md:flex-row items-center gap-4 justify-between bg-card text-card-foreground">
          <div className="flex items-center gap-2 w-full md:flex-1 md:max-w-sm relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search users..."
              className="pl-9"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1); // Reset page on search
              }}
            />
          </div>

          <div className="flex items-center gap-2 w-full md:w-auto">
            <div className="w-full md:w-45">
              <ManualFilter
                label="Role"
                value={role}
                onValueChange={(val) => {
                  setRole(val as RoleType | "all");
                  setPage(1);
                }}
                options={[
                  { value: "user", label: "User" },
                  { value: "admin", label: "Admin" },
                  { value: "superadmin", label: "Super Admin" },
                ]}
              />
            </div>
            <div className="w-full md:w-45">
              <ManualFilter
                label="Status"
                value={status}
                onValueChange={(val) => {
                  setStatus(val as "active" | "inactive" | "all");
                  setPage(1);
                }}
                options={[
                  { value: "active", label: "Active" },
                  { value: "inactive", label: "Inactive" },
                ]}
              />
            </div>
            <div className="w-full md:w-50">
              <ManualFilter
                label="Account"
                value={accountType ?? "all"}
                onValueChange={(val) => {
                  navigate({
                    search: (prev) => ({
                      ...prev,
                      accountType:
                        val === "all" ? undefined : (val as AccountType),
                      page: 1,
                    }),
                  });
                }}
                options={[
                  { value: "creator", label: "Creator" },
                  { value: "agency", label: "Agency" },
                  { value: "soft_deleted", label: "Soft Deleted" },
                  { value: "no_account", label: "No Account" },
                ]}
              />
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          {data ? (
            <UserDatatable
              data={data.data.map((user) => ({
                ...user,
                role: user.role ?? "user",
                profileCount: user.profileCount ?? 0,
                lastLoginAt: user.lastLoginAt ?? null,
              }))}
              page={page}
              total={data.meta.total}
              sortBy={sortBy}
              sortDir={sortDir}
              onSortChange={setSortBy}
              onPageChange={setPage}
              onUpdateRole={(userId, role) => {
                updateUserRole({ userId, role });
              }}
              onResetPassword={(userId) => {
                if (
                  confirm(
                    "Are you sure you want to remove the password for this user? They will need to use email OTP or reset it via 'Forgot Password'.",
                  )
                ) {
                  resetUserPassword({ userId });
                }
              }}
              onReset2FA={(userId) => {
                if (
                  confirm("Are you sure you want to disable 2FA for this user?")
                ) {
                  resetUser2FA({ userId });
                }
              }}
              onCreateAccount={(userId) => {
                if (
                  confirm(
                    "Create an org and profile for this user? This will allow them to access the platform.",
                  )
                ) {
                  createUserAccount({ userId });
                }
              }}
              onRestoreAccount={(orgId, orgName) => {
                setRestoreTarget({ orgId, orgName });
              }}
            />
          ) : (
            <div className="flex h-full items-center justify-center p-8 text-muted-foreground">
              {isLoading ? "Loading..." : "No users found"}
            </div>
          )}
        </div>
      </Card>

      <Dialog
        open={restoreTarget !== null}
        onOpenChange={(open) => {
          if (!open) closeRestoreDialog();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {conflictEmails ? "Email Conflicts Detected" : "Restore Account"}
            </DialogTitle>
            <DialogDescription>
              {conflictEmails ? (
                <>
                  The following email(s) are already verified by active profiles
                  and will have their verification cleared on restore:
                  <ul className="mt-2 list-disc pl-4 text-sm">
                    {conflictEmails.map((email) => (
                      <li key={email}>{email}</li>
                    ))}
                  </ul>
                  <span className="mt-2 block">
                    The email addresses will be preserved, but the restored
                    profiles will need to re-verify them.
                  </span>
                </>
              ) : (
                <>
                  This will restore the organization{" "}
                  <strong>{restoreTarget?.orgName}</strong> and all associated
                  profiles. The user will regain full access to the platform.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeRestoreDialog}
              disabled={isRestoringAccount}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (restoreTarget) {
                  restoreUserAccount({
                    orgId: restoreTarget.orgId,
                    force: conflictEmails !== null,
                  });
                }
              }}
              disabled={isRestoringAccount}
            >
              {isRestoringAccount
                ? "Restoring..."
                : conflictEmails
                  ? "Force Restore"
                  : "Restore Account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
